import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { searchProducts } from '../api/search'
import { PRODUCT_KIND, PRODUCT_STATUS } from '../constants/api'
import { QUERY_KEYS } from '../constants/query'
import { SEARCH_DEFAULTS, SEARCH_UI } from '../constants/search'
import { toApiMessage } from '../helpers/api-error'
import { mapSearchProductCard, type CatalogCard } from '../helpers/mapCatalog'
import { useDeliveryLocation } from './useDeliveryLocation'
import { storefrontCoordinates } from './useStorefrontCatalog'

type ProductSearchSuggestionsState = {
  products: CatalogCard[]
  loading: boolean
  error: string | null
}

export function useProductSearchSuggestions(
  query: string,
  enabled: boolean,
): ProductSearchSuggestionsState {
  const [debouncedQuery, setDebouncedQuery] = useState('')

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, SEARCH_DEFAULTS.debounceMs)
    return () => window.clearTimeout(handle)
  }, [query])

  const normalizedQuery = debouncedQuery
  const canSearch =
    enabled && normalizedQuery.length >= SEARCH_DEFAULTS.minQueryLength
  const { location } = useDeliveryLocation()
  const coords = storefrontCoordinates(location)
  const searchQuery = useQuery({
    queryKey: QUERY_KEYS.searchSuggestions(
      normalizedQuery,
      coords?.latitude ?? null,
      coords?.longitude ?? null,
    ),
    enabled: canSearch,
    queryFn: () =>
      searchProducts({
        q: normalizedQuery,
        status: PRODUCT_STATUS.active,
        kind: PRODUCT_KIND.standard,
        ...coords,
        sortBy: SEARCH_DEFAULTS.sortBy,
        sortOrder: SEARCH_DEFAULTS.sortOrder,
        page: SEARCH_DEFAULTS.page,
        limit: SEARCH_DEFAULTS.suggestionLimit,
      }),
  })
  const products = (searchQuery.data?.items ?? []).map((item, index) =>
    mapSearchProductCard(item, index),
  )
  const error = searchQuery.error
    ? toApiMessage(searchQuery.error, SEARCH_UI.errorFallback)
    : null
  const waitingOnDebounce =
    enabled && query.trim() !== normalizedQuery && query.trim().length > 0

  return useMemo(
    () => ({
      products,
      loading: waitingOnDebounce || (canSearch && searchQuery.isFetching),
      error,
    }),
    [canSearch, error, products, searchQuery.isFetching, waitingOnDebounce],
  )
}
